import React, { useMemo, useState } from "react";
import { parseEmails } from "../utils";
import { getProspections } from "../../../../../services/prospectionServices";

const emailKey = (e) => String(e || "").trim().toLowerCase();

function groupByEmail(rows = []) {
  const map = new Map();
  for (const r of rows) {
    const uniq = Array.from(new Set(parseEmails(r.email).map(emailKey))).filter(Boolean);
    for (const em of uniq) {
      if (!map.has(em)) map.set(em, []);
      map.get(em).push(r);
    }
  }
  // garder uniquement les groupes avec >1 entrée
  const groups = [];
  for (const [em, arr] of map.entries()) {
    if (arr.length > 1) {
      // tri lisible
      arr.sort((a, b) => {
        const A = `${a.nom_club || ""}`.toLowerCase();
        const B = `${b.nom_club || ""}`.toLowerCase();
        return A.localeCompare(B, "fr");
      });
      groups.push({ email: em, items: arr });
    }
  }
  // groupes les plus “doubles” en premier
  groups.sort((a, b) => b.items.length - a.items.length || a.email.localeCompare(b.email, "fr"));
  return groups;
}

export function DuplicateProspects({ rows = [], currentParams = {}, onEdit, onDelete }) {
  const [modeAll, setModeAll] = useState(false);
  const [loadingAll, setLoadingAll] = useState(false);
  const [allRows, setAllRows] = useState(null);

  const source = modeAll && allRows ? allRows : rows;
  const groups = useMemo(() => groupByEmail(source), [source]);

  const totalEntries = useMemo(() => groups.reduce((s, g) => s + g.items.length, 0), [groups]);

  const scanAll = async () => {
    try {
      setLoadingAll(true);
      setAllRows(null);
      // on récupère toutes les pages avec les filtres courants
      const limit = currentParams.limit || 500; // gros pas pour aller vite
      let page = 1;
      let acc = [];
      // 1er appel pour connaître la pagination
      const first = await getProspections({ ...currentParams, page, limit });
      const pages = first?.pagination?.pages || 1;
      acc = acc.concat(first?.data || []);
      for (page = 2; page <= pages; page++) {
        const res = await getProspections({ ...currentParams, page, limit });
        acc = acc.concat(res?.data || []);
      }
      setAllRows(acc);
    } catch (e) {
      console.error(e);
      // léger fallback: on reste en mode page
      setModeAll(false);
    } finally {
      setLoadingAll(false);
    }
  };

  if (!rows?.length) return null; // rien à afficher tant qu'on n'a pas de lignes

  return (
    <div className="mt-4 rounded-2xl p-4 bg-white dark:bg-slate-900 shadow-sm border border-slate-200 dark:border-slate-800">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
        <div>
          <div className="font-medium">Doublons d’e-mails</div>
          <div className="text-xs text-slate-500">
            {groups.length} e-mails en doublon • {totalEntries} entrées concernées
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-xs hidden md:block text-slate-500">Source :</div>
          <button
            onClick={() => setModeAll(false)}
            className={`px-3 py-1 rounded-lg text-sm border ${
              !modeAll ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "bg-white dark:bg-slate-900"
            }`}
            title="Détecte les doublons sur la page affichée"
          >
            Page courante
          </button>
          <button
            onClick={async () => {
              setModeAll(true);
              if (!allRows) await scanAll();
            }}
            disabled={loadingAll}
            className={`px-3 py-1 rounded-lg text-sm border ${
              modeAll ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "bg-white dark:bg-slate-900"
            } disabled:opacity-50`}
            title="Scanner toutes les pages selon les filtres"
          >
            {loadingAll ? "Scan…" : "Toutes les pages (filtres)"}
          </button>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className="text-sm text-slate-500 mt-3">Aucun doublon trouvé {modeAll ? "sur l’ensemble filtré" : "sur cette page"}.</div>
      ) : (
        <ul className="mt-3 divide-y divide-slate-200 dark:divide-slate-800 max-h-80 overflow-auto">
          {groups.map((g) => (
            <li key={g.email} className="py-3">
              <div className="flex items-center justify-between">
                <div className="font-mono text-sm">{g.email}</div>
                <span className="text-xs px-2 py-1 rounded-full bg-amber-100 text-amber-700">{g.items.length}×</span>
              </div>

              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {g.items.map((r) => (
                  <div
                    key={r.id}
                    className="rounded-lg border border-slate-200 dark:border-slate-800 p-2 flex items-center justify-between"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        {r.nom_club || "—"}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {r.discipline || "—"} • {r.departement_code || "—"}{r._isUnsub ? " • désinscrit" : ""}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => onEdit && onEdit(r)}
                        className="px-2 py-1 text-xs rounded bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
                      >
                        Éditer
                      </button>
                      <button
                        onClick={() => onDelete && onDelete(r.id)}
                        className="px-2 py-1 text-xs rounded bg-rose-50 hover:bg-rose-100 text-rose-700"
                      >
                        Suppr.
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default DuplicateProspects;
